import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import { IonButton } from '@ionic/react';
import MapboxDraw, { MapboxDrawControls } from '@mapbox/mapbox-gl-draw';
import {
  DrawCreateEvent,
  DrawDeleteEvent,
  DrawUpdateEvent,
} from '@mapbox/mapbox-gl-draw';
import { useEffect, useMemo, useState } from 'react';
import { useControl, useMap } from 'react-map-gl';

import { MAPBOX_DRAW_PLOT_OPTIONS } from './draw-options';

/* eslint-disable-next-line */
export interface DrawControlProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  limit?: number;
  onLoad?: (draw: MapboxDraw) => void;
  onCreate?: (e: DrawCreateEvent) => void;
  onUpdate?: (e: DrawUpdateEvent) => void;
  onDelete?: (e: DrawDeleteEvent) => void;
  onDeleteLine?: (e: DrawDeleteEvent) => void;
  onModeChange?: (mode: string) => void;
  initialPolygon?: GeoJSON.Feature<GeoJSON.MultiPolygon>;
  controls?: MapboxDrawControls;
  data?: GeoJSON.FeatureCollection<GeoJSON.Point>;
}

export function DrawControl(props: DrawControlProps) {
  const { current: map } = useMap();
  const [currentMode, setCurrentMode] = useState('');
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [pointsCount, setPointsCount] = useState(0);

  const draw = useMemo(
    () =>
      new MapboxDraw({
        ...MAPBOX_DRAW_PLOT_OPTIONS,
        controls: {
          point: false,
          line_string: false,
          combine_features: false,
          uncombine_features: false,
          ...props.controls,
        },
      }),
    []
  );

  useControl(() => draw, {
    position: props.position,
  });

  useEffect(() => {
    props.onLoad?.(draw);
  }, [props.onLoad]);

  // Function to finish the line string drawing
  const finishLine = () => {
    if (
      isDrawingLine &&
      currentMode.includes('draw_line_string') &&
      pointsCount > 1
    ) {
      draw.changeMode('simple_select');
      setIsDrawingLine(false);
    }
  };

  useEffect(() => {
    const onDrawCreate = (e: DrawCreateEvent) => {
      props.onCreate?.(e);
      // if (props.limit && draw.getAll().features.length > props.limit) {
      //   draw.delete(e.features[0].id as string);
      // } else {
      //   props.onCreate?.(e);
      // }
    };
    map?.on('draw.create', onDrawCreate);

    const onDrawUpdate = (e: DrawUpdateEvent) => {
      props.onUpdate?.(e);
    };
    map?.on('draw.update', onDrawUpdate);

    const onDrawDelete = (e: DrawDeleteEvent) => {
      props.onDelete?.(e);
    };
    map?.on('draw.delete', onDrawDelete);

    const onDrawDeleteLine = (e: DrawDeleteEvent) => {
      props.onDeleteLine?.(e);
    };
    map?.on('draw.delete', onDrawDelete);

    return () => {
      map?.off('draw.create', onDrawCreate);
      map?.off('draw.update', onDrawUpdate);
      map?.off('draw.delete', onDrawDelete);
      map?.off('draw.deleteLine', onDrawDeleteLine);
    };
  }, [props]);

  useEffect(() => {
    // Reset points count when mode changes
    const onModeChange = () => {
      const mode = draw.getMode();
      setCurrentMode(mode);
      setIsDrawingLine(mode.includes('draw_line_string'));

      if (mode.includes('draw_line_string')) {
        // Give a small delay to check for existing points in case we're continuing an existing line
        setTimeout(() => checkLinePoints(), 100);
      } else {
        setPointsCount(0);
      }

      props.onModeChange?.(mode);
    };
    map?.on('draw.modechange', onModeChange);

    return () => {
      map?.off('draw.modechange', onModeChange);
    };
  }, [props, draw]);

  useEffect(() => {
    const onDrawDrag = (e: any) => {
      if (e.features[0].geometry.type === 'Polygon') {
        draw.delete(e.features[0].id);
        draw.add(e.features[0]);
      }
    };
    map?.on('draw.drag', onDrawDrag);

    return () => {
      map?.off('draw.drag', onDrawDrag);
    };
  }, [props]);

  useEffect(() => {
    if (props.initialPolygon) {
      draw.deleteAll();
      draw.add(props.initialPolygon);
    }
  }, [props.initialPolygon]);

  useEffect(() => {
    // Track changes to the features being drawn
    const onDrawSelectionChange = () => {
      if (isDrawingLine && currentMode.includes('draw_line_string')) {
        // Get all features currently being drawn
        const features = draw.getAll().features;
        // Find the feature currently being drawn (the one in drawing mode)
        const currentFeature = features.find(
          (f) =>
            f.geometry.type === 'LineString' &&
            draw.getSelectedIds().includes(f.id as string)
        );

        // Count the points in the line
        const count = currentFeature?.geometry.coordinates?.length || 0;
        setPointsCount(count);
      }
    };

    // Listen for both draw events that might change the points count
    map?.on('draw.update', onDrawSelectionChange);
    map?.on('draw.selectionchange', onDrawSelectionChange);
    // This event is fired when a vertex is added
    map?.on('draw.modechange', onDrawSelectionChange);
    map?.on('mouseup', onDrawSelectionChange);

    return () => {
      map?.off('draw.update', onDrawSelectionChange);
      map?.off('draw.selectionchange', onDrawSelectionChange);
      map?.off('draw.modechange', onDrawSelectionChange);
      map?.off('mouseup', onDrawSelectionChange);
    };
  }, [isDrawingLine, currentMode, draw]);

  // Function to check for line points
  const checkLinePoints = () => {
    if (!draw) return;

    try {
      // Get all the current features
      const features = draw.getAll().features;

      // If we're in line string mode, the active feature is the one being edited
      const currentFeatures = features.filter(
        (f) => f.geometry.type === 'LineString'
      );

      if (currentFeatures.length > 0) {
        // Get the most recent LineString
        const lineFeature = currentFeatures[currentFeatures.length - 1];
        const count = lineFeature.geometry.coordinates?.length || 0;
        setPointsCount(count);
      } else {
        setPointsCount(0);
      }
    } catch (error) {
      setPointsCount(0);
    }
  };

  useEffect(() => {
    // Track when points are added to the line
    const trackLinePoints = () => {
      if (isDrawingLine && currentMode.includes('draw_line_string')) {
        checkLinePoints();
      }
    };

    // Listen for events that might change the points
    map?.on('click', trackLinePoints);
    map?.on('mouseup', trackLinePoints);
    map?.on('touchend', trackLinePoints);
    map?.on('draw.update', trackLinePoints);
    map?.on('draw.render', trackLinePoints);
    map?.on('draw.create', trackLinePoints);

    return () => {
      map?.off('click', trackLinePoints);
      map?.off('mouseup', trackLinePoints);
      map?.off('touchend', trackLinePoints);
      map?.off('draw.update', trackLinePoints);
      map?.off('draw.render', trackLinePoints);
      map?.off('draw.create', trackLinePoints);
    };
  }, [isDrawingLine, currentMode, draw]);

  return isDrawingLine ? (
    <div
      style={{
        position: 'absolute',
        bottom: '4px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        backgroundColor: '#fff',
        padding: 4,
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <IonButton
        disabled={pointsCount <= 1} // Disable if less than 2 points (need at least a start and end)
        onClick={finishLine}
      >
        Finish
      </IonButton>
    </div>
  ) : null;
}

export default DrawControl;
